import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row, Col, Nav, NavItem, NavLink, Badge } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { GridReadyEvent, IFilterDef } from "ag-grid-community";
import { useHistory, useLocation } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { setSelectedInvoiceId } from "../../../redux/reducers/TagLevel";
import {
  getInvoicesTagLevels,
  getJobTransactions2,
  getJobTransactions2TagLevels,
  deleteTransactionLine,
} from "../../../docuclipper/api";
import { getCustomUi } from "../../Docuclipper/ConfirmAlert/ConfirmAlert";
import { ReduxState } from "../../../redux";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import {
  setExternalFiltersColumnDefs,
  clearExternalFilters,
} from "src/redux/reducers/ExternalFilter";
import { TableFilterConfig } from "src/docuclipper/DocuclipperTypes";
import { datefilterParamsYYYYMMDD } from "../../Pages/Analyze/AnalyzeTransactionsTable";
import { createAlert } from "src/redux/reducers/Alerts";
import { convertFilterModel, convertSortModel } from "src/utils/filters";
import AddDocumentsModal from "../DataArchive/TransactionManager/AddDocumentsModal";
import AddDocumentsPopover from "../DataArchive/TransactionManager/AddBankStatementsPdfCsvPopover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { setDownloadInvoiceModalOpen } from "../../../redux/reducers/JobExport";
import { DownloadInvoiceModal } from "../DataArchive/TransactionManager/DownloadInvoiceModal";
import { useRecentDocument } from "src/hooks/useRecentNavigation";
import { SUPPORTED_SCOPES, fetchTagLevelIntegration } from "src/redux/reducers/TagLevelIntegration";
import { fetchIntegrations } from "src/redux/reducers/Integration";
import { fetchTagLevels } from "src/redux/reducers/TagLevel";
import InvoicePage from "../Invoices/InvoicePage";

const getType = (documentType) => {
  if (["payables", "bill", "expense"].includes(documentType)) {
    return "payables";
  } else if (["receivables", "invoice"].includes(documentType)) {
    return "receivables";
  }
  throw new Error(`Unknown document type: ${documentType}`);
};

const formatData = (data) => {
  // Ensure data is an array before calling map
  if (!Array.isArray(data)) {
    console.warn("formatData received non-array data:", data);
    return [];
  }

  return data.map((item) => {
    // Parse invoice row to check sync status
    let invoiceRow = null;
    let isSynced = false;
    try {
      if (item.row) {
        invoiceRow = typeof item.row === 'string' ? JSON.parse(item.row) : item.row;
        isSynced = !!(invoiceRow?.link || invoiceRow?.status === "synced" || invoiceRow?.status === "imported");
      }
    } catch (e) {
      // Ignore parse errors
    }

    // Check metadata for sync info
    let metadataSyncInfo = null;
    try {
      if (item.metadata) {
        const metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata;
        if (metadata?.integrations) {
          // Check if any integration has synced
          const integrations = metadata.integrations;
          metadataSyncInfo = Object.values(integrations).some((integration: any) => 
            integration?.syncedAt || integration?.id
          );
        }
      }
    } catch (e) {
      // Ignore parse errors
    }

    return {
      id: item.transactionLineId,
      invoiceId: item.invoiceId,
      invoiceDate: item.documentDate,
      invoiceVendor: item.vendorNormalized || "",
      invoiceCustomer: item.customerNormalized || "",
      invoiceAmount: parseFloat(item.amount) || 0,
      invoiceType: getType(item.documentType),
      docType: item.documentType,
      approvalStatus: item.approvalStatus || "",
      needsApproval: item.needsApproval || false,
      isSynced: isSynced || metadataSyncInfo || false,
      hasPOMatch: String(Boolean(item.hasPOMatch)),
      hasBankMatch: String(Boolean(item.hasBankMatch)),
    };
  });
};

type TabType = "all" | "needsApproval" | "notSynced";

export const Invoices = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const gridApi = useRef<any>(null);
  const editingInvoiceId = new URLSearchParams(location.search).get("invoiceId");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [hasData, setHasData] = useState<boolean | null>(null); // null = loading, true = has data, false = empty
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [tabCounts, setTabCounts] = useState<{
    all: number | null;
    needsApproval: number | null;
    notSynced: number | null;
  }>({
    all: null,
    needsApproval: null,
    notSynced: null,
  });

  const { externalFilters } = useSelector(
    (state: ReduxState) => state.ExternalFilter
  );
  const job = useSelector((state: ReduxState) => state.JobData.job);

  const { selectedL1: l1, selectedL2: l2, tagLevels, loading: tagLevelsLoading } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const { integrations, loading: integrationsLoading } = useSelector(
    (state: ReduxState) => state.Integration
  );

  const linkedIntegrationDetails = useSelector(
    (state: ReduxState) => state.TagLevelIntegration.linkedIntegrationDetails
  );

  // Get current tagLevelId from l1/l2
  const currentTagLevelId = useMemo(() => {
    if (!l1 || !l2 || !tagLevels?.root) return null;
    return (tagLevels.root as any)?.[l1]?.[l2]?.metadata?.tagId || null;
  }, [l1, l2, tagLevels]);

  // Fetch integrations and tag levels on mount
  useEffect(() => {
    dispatch(fetchIntegrations());
    dispatch(fetchTagLevels());
  }, [dispatch]);

  // Fetch integration for current tag level (after integrations and tag levels are loaded).
  // Re-run when integrations load so we can resolve the link (e.g. first load had empty integrations).
  useEffect(() => {
    if (!integrationsLoading && !tagLevelsLoading && currentTagLevelId) {
      dispatch(fetchTagLevelIntegration());
    }
  }, [currentTagLevelId, dispatch, integrationsLoading, tagLevelsLoading, integrations.length]);

  // Check if integration is connected (Xero or QuickBooks)
  const hasIntegration = useMemo(() => {
    return !!(
      linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero ||
      linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.quickbooks
    );
  }, [linkedIntegrationDetails]);

  // Fetch counts for all tabs when l1/l2 changes
  useEffect(() => {
    if (!l1 || !l2) return;

    // Fetch count for "all" tab
    getInvoicesTagLevels(l1, l2, {
      pagination: { page: 1, pageSize: 1 },
      filters: [],
      sorting: [],
    })
      .then((response) => {
        setTabCounts((prev) => ({
          ...prev,
          all: response.pagination.totalCount,
        }));
      })
      .catch((err) => {
        console.error("Error fetching all invoices count:", err);
      });

    // Fetch count for "needsApproval" tab
    // We fetch pending status and use that as the count
    // The actual count (including other non-approved statuses) will be updated when the tab is visited
    getInvoicesTagLevels(l1, l2, {
      pagination: { page: 1, pageSize: 1 },
      filters: [
        {
          field: "approvalStatus",
          operator: "equals",
          value: "pending",
        },
      ],
      sorting: [],
    })
      .then((response) => {
        // Use pending count as initial approximation
        // Will be refined when tab is visited with client-side filtering
        setTabCounts((prev) => ({
          ...prev,
          needsApproval: response.pagination.totalCount,
        }));
      })
      .catch((err) => {
        console.error("Error fetching needs approval count:", err);
      });

    // Fetch count for "notSynced" tab (only if integration exists)
    if (hasIntegration) {
      getInvoicesTagLevels(l1, l2, {
        pagination: { page: 1, pageSize: 1 },
        filters: [
          {
            field: "isSynced",
            operator: "equals",
            value: false,
          },
        ],
        sorting: [],
      })
        .then((response) => {
          setTabCounts((prev) => ({
            ...prev,
            notSynced: response.pagination.totalCount,
          }));
        })
        .catch((err) => {
          console.error("Error fetching not synced count:", err);
        });
    }
  }, [l1, l2, hasIntegration]);

  // Track this page view as a recent document
  useRecentDocument(
    l1 && l2
      ? {
          docType: "invoices",
          label: `Invoices - ${l2}`,
          icon: "file-invoice",
          l1: l1,
          l2: l2,
        }
      : null
  );

  const types = [
    {
      label: "Receivables / Invoices",
      value: "receivables",
    },
    {
      label: "Payables / Bills / Expenses",
      value: "payables",
    },
  ];

  const docTypes = [
    {
      label: "Payables",
      value: "payables",
    },
    {
      label: "Bill",
      value: "bill",
    },
    {
      label: "Expense",
      value: "expense",
    },
    {
      label: "Receivables",
      value: "receivables",
    },
    {
      label: "Invoice",
      value: "invoice",
    },
  ];

  const externalFilterColumns = useMemo(
    () => [
      {
        field: "invoiceId",
        label: "Invoice ID",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "invoiceDate",
        label: "Date",
        filter: true,
        filterType: "date",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceAmount",
        label: "Amount",
        filter: true,
        filterType: "number",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceType",
        label: "Type",
        filter: true,
        filterType: "select",
        options: types,
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceVendor",
        label: "Vendor",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "invoiceCustomer",
        label: "Customer",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "approvalStatus",
        label: "Approval Status",
        filter: true,
        filterType: "select",
        options: [
          { label: "Pending", value: "pending" },
          { label: "Approved", value: "approved" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "hasPOMatch",
        label: "Has PO Match",
        filter: true,
        filterType: "select",
        options: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "hasBankMatch",
        label: "Has Bank Match",
        filter: true,
        filterType: "select",
        options: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
    ],
    []
  );

  const getRows = useCallback(
    (params) => {
      if (!l1 || !l2) {
        // console.log("missing l1 or l2");
        params.failCallback();
        setHasData(false);
        return;
      }

      // Build filters based on active tab
      const tabFilters = [];
      
      if (activeTab === "needsApproval") {
        // Filter for invoices that need approval (status is pending or not approved)
        // We'll filter for pending status, and also check client-side for other non-approved statuses
        tabFilters.push({
          field: "approvalStatus",
          operator: "equals",
          value: "pending",
        });
      } else if (activeTab === "notSynced" && hasIntegration) {
        // Filter for invoices that haven't synced
        tabFilters.push({
          field: "isSynced",
          operator: "equals",
          value: false,
        });
      }

      try {
        getInvoicesTagLevels(l1, l2, {
          pagination: {
            page: Math.floor(params.startRow / pagination.pageSize) + 1,
            pageSize: params.endRow - params.startRow,
          },
          filters: [
            ...convertFilterModel(params),
            ...tabFilters,
          ],
          sorting: convertSortModel(params.sortModel),
        })
          .then((response) => {
            // console.log({ invoiceData: response.data });
            let formattedData = formatData(response.data);
            
            // Apply client-side filtering based on active tab
            if (activeTab === "needsApproval") {
              // Filter for invoices that need approval (status is not "approved")
              // This includes pending, rejected, or any other non-approved status
              formattedData = formattedData.filter(
                (item) => item.approvalStatus && item.approvalStatus !== "approved"
              );
            }
            // Note: "notSynced" filtering is now done server-side via isSynced filter
            
            // Update pagination
            // For "needsApproval", we do client-side filtering so use filtered count
            // For other tabs, use server response pagination
            const finalCount = activeTab === "needsApproval" 
              ? formattedData.length 
              : response.pagination.totalCount;
            
            setPagination({
              ...response.pagination,
              totalCount: finalCount,
              totalPages: Math.ceil(finalCount / pagination.pageSize),
            });
            setHasData(finalCount > 0);
            
            // Update tab counts based on current tab
            setTabCounts((prev) => ({
              ...prev,
              [activeTab]: finalCount,
            }));
            
            params.successCallback(
              formattedData,
              finalCount
            );
          })
          .catch((err) => {
            params.failCallback();
            setHasData(false);
            dispatch(
              createAlert({
                message: err.message || "Error fetching invoices",
                timeout: 0,
                type: "error",
              })
            );
          });
      } catch (err) {
        params.failCallback();
        setHasData(false);
        dispatch(
          createAlert({
            message:
              err instanceof Error ? err.message : "Error fetching invoices",
            timeout: 0,
            type: "error",
          })
        );
      }
    },
    [job, pagination.pageSize, externalFilters, l1, l2, activeTab, hasIntegration]
  );

  const dataSource = useMemo(
    () => ({
      rowCount: undefined,
      getRows,
    }),
    [getRows]
  );

  // Reset hasData when tab changes to prevent showing empty state prematurely
  useEffect(() => {
    setHasData(null); // Set to loading state when tab changes
  }, [activeTab]);

  useEffect(() => {
    if (gridApi.current) {
      // console.log("purgeInfiniteCache");
      gridApi.current.purgeInfiniteCache();
      // Reset to first page when tab changes
      gridApi.current.paginationGoToPage(0);
      // Refresh the datasource when tab changes
      gridApi.current.setDatasource(dataSource);
    }
  }, [l1, l2, activeTab, dataSource]);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      // Reset hasData to null when grid is ready (loading state)
      setHasData(null);
      // Set the datasource which will trigger getRows
      gridApi.current.setDatasource(dataSource);

      // Initialize external filters after grid is ready
      dispatch(clearExternalFilters());
      dispatch(
        setExternalFiltersColumnDefs({
          externalFilterColumnDefs: externalFilterColumns,
        })
      );
    },
    [dataSource, dispatch, externalFilterColumns]
  );

  const SelectInvoiceRenderer = (props: any) => {
    if (!props.data || !props.data.id) {
      return <></>;
    }
    const handleClick = () => {
      dispatch(setSelectedInvoiceId(props.data.id));
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("invoiceId", String(props.data.id));
      history.replace(`${location.pathname}?${searchParams.toString()}`);
    };

    const handleDeleteClick = () => {
      const invoiceId = props.data.id;

      const getMessage = () => (
        <div style={{ marginBottom: "20px" }}>
          <span>{`Are you sure you want to delete invoice with ID: ${invoiceId}?`}</span>
        </div>
      );

      confirmAlert({
        customUI: getCustomUi({
          onConfirm: () => {
            deleteTransactionLine(parseInt(invoiceId))
              .then(() => {
                dispatch(
                  createAlert({
                    message: `Invoice with ID ${invoiceId} deleted successfully.`,
                    timeout: 3000,
                    type: "success",
                  })
                );
                if (gridApi.current) {
                  gridApi.current.purgeInfiniteCache();
                  gridApi.current.setDatasource({
                    rowCount: undefined,
                    getRows,
                  });
                }
              })
              .catch((err) => {
                dispatch(
                  createAlert({
                    message: err.message || "Error deleting invoice.",
                    timeout: 0,
                    type: "error",
                  })
                );
              });
          },
          confirmCTA: "Delete Invoice",
          cancelCTA: "Cancel",
          getMessage,
        }),
      });
    };

    return (
      <div className="d-flex align-items-center">
        <Button variant="link" size="sm" onClick={handleClick}>
          <FontAwesomeIcon icon="pencil-alt" className="mr-1" />
          Edit invoice
        </Button>
        <Button
          variant="link"
          size="sm"
          onClick={handleDeleteClick}
          className="ml-2 text-danger"
        >
          <FontAwesomeIcon icon="trash" />
        </Button>
      </div>
    );
  };

  const columnDefs = useMemo(
    () => [
      {
        field: "invoiceId",
        headerName: "Invoice ID",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceType",
        headerName: "Type",
        sortable: true,
        filter: true,
      },
      {
        field: "docType",
        headerName: "Document Type",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceVendor",
        headerName: "Vendor",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceCustomer",
        headerName: "Customer",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceAmount",
        headerName: "Amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        valueFormatter: (params: any) =>
          params.value
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(params.value)
            : "",
      },
      {
        field: "invoiceDate",
        headerName: "Date",
        sortable: true,
        filter: "agDateColumnFilter",
        filterParams: datefilterParamsYYYYMMDD,
      },
      {
        field: "approvalStatus",
        headerName: "Approval Status",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) => {
          if (!params.value) return "";
          return params.value.charAt(0).toUpperCase() + params.value.slice(1);
        },
      },
      {
        field: "hasPOMatch",
        headerName: "Has PO Match",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) =>
          params.value === "true" ? "Yes" : "No",
      },
      {
        field: "hasBankMatch",
        headerName: "Has Bank Match",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) =>
          params.value === "true" ? "Yes" : "No",
      },
      {
        headerName: "Actions",
        cellRenderer: SelectInvoiceRenderer,
        sortable: false,
        filter: false,
        width: 180,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      flex: 1,
      filter: true,
      sortable: true,
    }),
    []
  );

  const handleDocumentAction = () => {
    const currentParams = new URLSearchParams(window.location.search);
    history.push(
      `/extract/invoice/${
        currentParams.toString() ? "?" + currentParams.toString() : ""
      }`
    );
  };

  // Empty State Component
  const EmptyStateComponent = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: "400px" }}
    >
      <div className="text-center mb-4">
        <FontAwesomeIcon
          icon="file-invoice"
          size="4x"
          className="text-muted mb-3"
        />
        <h4 className="text-muted">No Invoices Found</h4>
        <p className="text-muted">
          Get started by adding your invoices, receipts, or bills
        </p>
      </div>

    </div>
  );

  // Check if we should show empty state (only when no filters are applied and no data)
  // Don't show empty state if we're loading (hasData === null)
  const showEmptyState = hasData === false && Object.keys(externalFilters).length === 0 && hasData !== null;

  const closeInlineEditor = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("invoiceId");
    const qs = searchParams.toString();
    history.replace(qs ? `${location.pathname}?${qs}` : location.pathname);
  };

  if (editingInvoiceId) {
    return (
      <div className="p-4">
        <Button variant="link" size="sm" className="mb-3 pl-0" onClick={closeInlineEditor}>
          <FontAwesomeIcon icon="arrow-left" className="mr-1" />
          Back to list
        </Button>
        <InvoicePage />
      </div>
    );
  }

  return (
    <div className="p-4">
      {l1 && l2 && (
        <Row className="justify-content-end mb-3">
          <Col className="d-flex justify-content-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => dispatch(setDownloadInvoiceModalOpen(true))}
            >
              <FontAwesomeIcon icon="download" className="mx-2" />
              Download Invoices
            </Button>
            <DownloadInvoiceModal />
          </Col>
        </Row>
      )}
      {/* Tabs - Always show tabs */}
      <Row className="mb-3">
        <Col md="12">
          <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k as TabType)}>
            <NavItem>
              <NavLink eventKey="all" className="d-flex align-items-center justify-content-between">
                <span>All Invoices</span>
                {tabCounts.all !== null && (
                  <Badge 
                    pill
                    variant="light" 
                    className="ml-2"
                    style={{ 
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      padding: "3px 8px",
                      backgroundColor: "#e9ecef",
                      color: "#495057",
                      minWidth: "24px",
                      textAlign: "center"
                    }}
                  >
                    {tabCounts.all}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink eventKey="needsApproval" className="d-flex align-items-center justify-content-between">
                <span>Needs Approval</span>
                {tabCounts.needsApproval !== null && (
                  <Badge 
                    pill
                    variant="light" 
                    className="ml-2"
                    style={{ 
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      padding: "3px 8px",
                      backgroundColor: "#e9ecef",
                      color: "#495057",
                      minWidth: "24px",
                      textAlign: "center"
                    }}
                  >
                    {tabCounts.needsApproval}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
            {hasIntegration && (
              <NavItem>
                <NavLink eventKey="notSynced" className="d-flex align-items-center justify-content-between">
                  <span>Not Synced</span>
                  {tabCounts.notSynced !== null && (
                    <Badge 
                      pill
                      variant="light" 
                      className="ml-2"
                      style={{ 
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        padding: "3px 8px",
                        backgroundColor: "#e9ecef",
                        color: "#495057",
                        minWidth: "24px",
                        textAlign: "center"
                      }}
                    >
                      {tabCounts.notSynced}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            )}
          </Nav>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md="12">
          <Row className="px-3">
            <span className="ml-2 mr-2">Filter by</span>
            {gridApi.current && (
              <ExternalFilter
                columnDefs={externalFilterColumns}
                gridApi={gridApi.current}
              />
            )}
          </Row>
        </Col>
      </Row>

      {/* Show empty state if no data and no filters, otherwise show grid */}
      {showEmptyState ? (
        <Row>
          <Col md="12">
            <EmptyStateComponent />
          </Col>
        </Row>
      ) : (
        <>
          {hasData === false && Object.keys(externalFilters).length > 0 && (
            <Row className="mb-3">
              <Col md="12">
                <div className="alert alert-info text-center">
                  <FontAwesomeIcon icon="info-circle" className="mr-2" />
                  No invoices match the current filters. Try adjusting your filters or switch to a different tab.
                </div>
              </Col>
            </Row>
          )}
          <div
            className="ag-theme-alpine"
            style={{ height: "500px", width: "100%" }}
          >
            <AgGridReact
              key={`invoice-grid-${activeTab}-${l1}-${l2}`}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              rowModelType="infinite"
              cacheBlockSize={pagination.pageSize}
              cacheOverflowSize={2}
              maxConcurrentDatasourceRequests={1}
              infiniteInitialRowCount={1}
              maxBlocksInCache={10}
              pagination={true}
              paginationPageSize={pagination.pageSize}
              suppressCellFocus={true}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Invoices;
